import React, { useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { GrDocumentDownload } from 'react-icons/gr';
import FormNoteModal from '../../../../components/FormNoteModal/FormNoteModal';
import LabelReadMoreReadLess from '../../../../components/LabelReadMoreReadLess/LabelReadMoreReadLess';
import EditorCustom from '../../../../components/EditorCustorm/EditorCustom';
import DialogDeleteCustom from '../../../../components/DialogDeleteCustom/DialogDeleteCustom';
import { getAll, deleteNoteContact, downloadNoteFile, addNoteContact, editNoteContact } from '../../../../services/NoteContact';
import { getUserData } from '../../../../utils/FunctionHelpers';
import { withSnackbar } from '../../../../utils/SnackbarHOC';
import { removeLineBreak } from '../../../../utils/EditorUtil';
import { commonGridSx, gridLocaleText, makeGridToolbar } from '../../../../utils/gridStyles';
import { DataGrid } from '../../../../components/SortableDataGrid/SortableDataGrid';

const CustomToolbar = makeGridToolbar('Notes');

function Notes({ snackbarShowMessage, onNoteAdded, ...rest }) {
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [notesContact, setNotesContact] = useState([]);
  const [noteContactInfo, setNoteContactInfo] = useState({ auteur: '', id: '' });
  const [openDialogDelete, setOpenDialogDelete] = useState(false);
  const [loadingToDelete, setLoadingToDelete] = useState(false);
  const [reloadingData, setReloadingData] = useState(false);
  const [textEdit, setTextEdit] = useState(true);
  const [comment, setComment] = useState('');
  const [pj, setPj] = useState(null);
  const [errors, setErrors] = useState({});
  const [modalState, setModalState] = useState({ open: false });
  const [idNoteSeleted, setIdNoteSeleted] = useState(0);
  const parametters = useParams();
  const idContact = rest.uuid ?? parametters.idContact;

  React.useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setNotesContact([]);
      try {
        const res = await getAll(page + 1, idContact);
        if (!active) return;
        setNotesContact(res.data.notesContact || []);
        setRowCount(res.data.count || 0);
      } catch {
        snackbarShowMessage('Erreur de chargement des notes.', 'error', 4000);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [page, reloadingData, idContact]);

  const handleOpenDialog = (row) => { setNoteContactInfo({ auteur: row.auteur, id: row.id }); setOpenDialogDelete(true); };
  const handleCloseDialog = () => { if (!loadingToDelete) setOpenDialogDelete(false); };

  const handleDownload = (noteId, pjFile) => {
    const FileDownload = require('js-file-download');
    (async () => {
      setLoading(true);
      try {
        const response = await downloadNoteFile(noteId, pjFile);
        FileDownload(response.data, pjFile);
      } catch {
      } finally {
        setLoading(false);
      }
    })();
  };

  const handleDeleteNote = () => {
    (async () => {
      setLoadingToDelete(true);
      try {
        snackbarShowMessage('Patientez svp!', 'warning', null);
        const res = await deleteNoteContact(noteContactInfo.id);
        snackbarShowMessage(res.data);
        handleCloseDialog();
        setReloadingData(!reloadingData);
        if (onNoteAdded) onNoteAdded();
      } catch {
        snackbarShowMessage('Erreur de suppression de la données, veuillez réssayer plutard.', 'error', 4000);
      } finally {
        setLoadingToDelete(false);
      }
    })();
  };

  const validate = (c) => {
    const temp = { commentaire: !(c?.replace(/<\/?[^>]+(>|$)/g, '') === '') ? '' : "Le champ 'COMMENTAIRE' est obligatoire." };
    setErrors(temp);
    return Object.values(temp).every((x) => x === '');
  };

  const handleOpen = () => setModalState({ open: true });
  const handleClose = () => { if (loading) return; setModalState({ open: false }); setComment(''); setErrors(''); };

  const handleSubmit = (c) => {
    if (validate(c)) {
      (async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append('dateTime', new Date());
        formData.append('commentaire', removeLineBreak(c));
        formData.append('auteur', getUserData().uuid);
        formData.append('contact', idContact);
        formData.append('pj', pj);
        try {
          snackbarShowMessage('Patientez svp!', 'warning', null);
          const res = await addNoteContact(formData);
          snackbarShowMessage(res.data);
          setReloadingData(!reloadingData);
          handleClose();
          if (onNoteAdded) onNoteAdded();
        } catch (e) {
          snackbarShowMessage("Erreur d'ajout de note: " + e.message, 'error', 4000);
          handleClose();
        } finally {
          setLoading(false);
        }
      })();
    }
  };

  const handleUpdate = (c) => {
    if (validate(c)) {
      (async () => {
        setLoading(true);
        const formData = new FormData();
        formData.append('commentaire', removeLineBreak(c));
        formData.append('pj', pj);
        try {
          snackbarShowMessage('Patientez svp!', 'warning', null);
          const response = await editNoteContact(formData, idNoteSeleted);
          snackbarShowMessage(response.data);
          setReloadingData(!reloadingData);
          setTextEdit(true);
          handleClose();
          if (onNoteAdded) onNoteAdded();
        } catch {
          snackbarShowMessage('Une erreur est survenue. veuillez réessayer plus tard.', 'error', 4000);
          handleClose();
        } finally {
          setLoading(false);
        }
      })();
    }
  };

  const editorCustomProps = { value: comment, setValue: setComment, handleSubmit: handleUpdate, enableOnBlur: true };
  const handleRowClick = (c, noteId) => { setComment(c); setIdNoteSeleted(noteId); setTextEdit(false); };

  const columns = [
    {
      field: 'dateTime', headerName: 'DATE/HEURE', width: 150,
      renderCell: (p) => (
        <Typography sx={{ fontSize: '1rem', color: '#64748b' }}>
          {p.value?.date ? moment(p.value.date).format('DD/MM/YYYY HH:mm') : '—'}
        </Typography>
      ),
    },
    {
      field: 'commentaire', headerName: 'COMMENTAIRES', flex: 1, minWidth: 200, sortable: false,
      renderCell: (p) => (
        <Box sx={{ width: '100%', cursor: 'text' }}>
          {textEdit ? (
            <span onDoubleClick={() => handleRowClick(p.value, p.row.id)}>
              <LabelReadMoreReadLess data={p.value} />
            </span>
          ) : p.row.id === idNoteSeleted ? (
            <EditorCustom {...editorCustomProps} />
          ) : (
            <LabelReadMoreReadLess data={p.value} />
          )}
        </Box>
      ),
    },
    {
      field: 'auteur', headerName: 'AUTEUR', width: 180,
      renderCell: (p) => <Typography sx={{ fontSize: '1rem', color: '#475569' }}>{p.value || '—'}</Typography>,
    },
    {
      field: 'pj', headerName: 'P.J', width: 80, sortable: false,
      renderCell: (p) => p.value ? (
        <Tooltip title="Télécharger">
          <IconButton size="small" onClick={() => handleDownload(p.row.id, p.row.pj)}
            sx={{ color: '#03407b', p: 0.5, borderRadius: 1 }}>
            <GrDocumentDownload style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : null,
    },
    {
      field: 'action', headerName: 'ACTION', width: 130, sortable: false, filterable: false,
      renderCell: (p) => (
        <Tooltip title="Supprimer">
          <IconButton size="small" onClick={() => handleOpenDialog(p.row)}
            sx={{ color: '#ef4444', background: '#fef2f2', borderRadius: '8px', '&:hover': { background: '#fee2e2' }, p: 0.75 }}>
            <Delete sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const rows = (notesContact || []).map((n) => ({ ...n }));

  return (
    <>
      <DialogDeleteCustom
        handleClose={handleCloseDialog} open={openDialogDelete} handleConfirm={handleDeleteNote}
        loading={loadingToDelete} label={`Voulez vous vraiment supprimer la note de ${noteContactInfo?.auteur}?`}
      />
      <FormNoteModal errors={errors} setPj={setPj} loading={loading} handleSubmit={handleSubmit}
        modalState={modalState.open} handleOpen={handleOpen} handleClose={handleClose} />
      <Box sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '44vh', minHeight: 300, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.04)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize: 50 }}
          onPaginationModelChange={(m) => setPage(m.page)}
          pageSizeOptions={[50]}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          showToolbar
          slots={{ toolbar: CustomToolbar }}
          slotProps={{ loadingOverlay: { variant: 'skeleton', noRowsVariant: 'skeleton' } }}
          localeText={gridLocaleText}
          sx={{ ...commonGridSx, '& .MuiDataGrid-cell': { py: 0.75, alignItems: 'flex-start' } }}
        />
      </Box>
    </>
  );
}

export default withSnackbar(Notes);

